// src/components1/VoiceAddSheet.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import useSpeechInput from "../utils/useSpeechInput";
import { ThemedDatePicker } from "./ThemedCalendar";
import { parseExpenseFromSpeech } from "../service/AIService";

/**
 * Say an expense, check what was heard, save it.
 *
 * Three states in one sheet: listening (or typing, where the browser cannot
 * listen), reading it back, and confirming the fields. Nothing is written until
 * the user presses Save, and an amount that was never spoken is asked for
 * rather than invented.
 */

const STATE = { INPUT: "input", PARSING: "parsing", CONFIRM: "confirm" };

const VoiceAddSheet = ({
  closing = false,
  categories = [],
  currency = "$",
  scope = "personal",
  todayLocal,
  onSave,
  onClose,
  onManual,
}) => {
  const [stage, setStage] = useState(STATE.INPUT);
  // one sentence, whether it was dictated or typed — always editable, so a
  // misheard word can be fixed before it is parsed
  const [sentence, setSentence] = useState("");
  const [heard, setHeard] = useState("");
  const [draft, setDraft] = useState(null);
  const [extra, setExtra] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const amountRef = useRef(null);
  const startedRef = useRef(false);

  const runParse = useCallback(
    async (sentence) => {
      setHeard(sentence);
      setStage(STATE.PARSING);
      setError("");
      try {
        const { items } = await parseExpenseFromSpeech({
          transcript: sentence,
          categories,
          currency,
          scope,
          todayLocal,
        });
        setDraft(items[0]);
        setExtra(items.slice(1));
        setStage(STATE.CONFIRM);
      } catch (err) {
        setError(err.message || "Could not read that back.");
        setStage(STATE.INPUT);
      }
    },
    [categories, currency, scope, todayLocal]
  );

  // Finishing a sentence fills the fields by itself: stopping the mic is the
  // signal that it is ready to read back, so there is nothing else to press.
  const speech = useSpeechInput({
    onFinal: (said) => {
      setSentence(said);
      if (said.trim().length > 2) runParse(said.trim());
    },
  });
  const { supported, listening, transcript, start, stop } = speech;

  // keep the box showing what is being heard, live
  useEffect(() => {
    if (listening && transcript) setSentence(transcript);
  }, [listening, transcript]);

  // start listening as the sheet opens; typing is the path where it cannot
  useEffect(() => {
    if (supported && !startedRef.current) {
      startedRef.current = true;
      start();
    }
  }, [supported, start]);

  // the amount is the one field worth landing on when it was not caught
  useEffect(() => {
    if (stage === STATE.CONFIRM && draft && draft.missing.includes("amount") && amountRef.current) {
      amountRef.current.focus();
    }
  }, [stage, draft]);

  const closeAll = () => {
    stop();
    onClose();
  };

  const setField = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
      missing: prev.missing.filter((m) => m !== field),
    }));
  };

  const amountReady = Number(draft?.amount) > 0;
  // nothing in the list fitted what was said: the category needs a human
  const categoryUnsure = !!draft?.missing.includes("category");
  const ready = sentence.trim().length > 0;
  // nothing heard, but the microphone still works: offer another go
  const canRetry = supported && !listening && !!speech.error;

  return (
    <div className="bottom-sheet-overlay" onClick={closeAll}>
      <div className={`bottom-sheet-content voice-sheet${closing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="bottom-sheet-handle" onClick={closeAll}></div>
        <button type="button" className="bottom-sheet-close-corner" onClick={closeAll} aria-label="Close">
          <i className="fi fi-rr-cross"></i>
        </button>

        {/* ---------------- say it, or type it ---------------- */}
        {stage === STATE.INPUT && (
          <>
            <div className="voice-head">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                {listening ? "Listening…" : supported ? "Ready when you are" : "Say it in words"}
              </h5>
              <small className="text-muted">
                {listening ? "Stop when you are done — the fields fill themselves" : "Amount, what it was for, and where"}
              </small>
            </div>

            {listening && (
              <div className="voice-meter" aria-hidden="true">
                {[18, 34, 56, 72, 48, 78, 62, 40, 66, 28, 16].map((h, i) => (
                  <span key={i} style={{ height: `${h}px`, animationDelay: `${i * 90}ms` }}></span>
                ))}
              </div>
            )}

            <div className="voice-typed-wrap">
              <label htmlFor="voiceSentence" className="voice-transcript-label">
                {supported ? "What I hear — edit it if I got a word wrong" : "Type it, or use the mic key on your keyboard"}
              </label>
              <textarea
                id="voiceSentence"
                className="modern-input voice-typed"
                rows={3}
                value={sentence}
                onChange={(e) => setSentence(e.target.value)}
                placeholder="add 50 dollars to groceries at Kroger"
              />
            </div>

            {(error || speech.error) && <div className="voice-error">{error || speech.error}</div>}

            <div className="voice-actions">
              {supported && (
                <button
                  type="button"
                  className={`voice-mic-btn${listening ? " is-live" : ""}`}
                  onClick={() => (listening ? stop() : start())}
                  aria-label={listening ? "Stop listening" : "Start listening"}
                >
                  <i className="fi fi-rr-microphone"></i>
                </button>
              )}
              <button
                type="button"
                className="btn-coral-modern voice-parse-btn"
                disabled={!listening && !ready && !canRetry}
                onClick={() => {
                  if (listening) {
                    // finishing is the action while listening: stopping reads
                    // it back by itself
                    stop();
                    return;
                  }
                  if (!ready && canRetry) {
                    start();
                    return;
                  }
                  runParse(sentence.trim());
                }}
              >
                {listening ? "Done" : !ready && canRetry ? "Listen again" : "Read it back"}
              </button>
            </div>

            <button type="button" className="voice-secondary-btn" onClick={onManual}>
              Fill the form instead
            </button>
          </>
        )}

        {/* ---------------- waiting on Gemini ---------------- */}
        {stage === STATE.PARSING && (
          <div className="voice-parsing">
            <div className="voice-parsing-dots" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="voice-parsing-title">Reading that back…</div>
            <div className="voice-said">&ldquo;{heard}&rdquo;</div>
          </div>
        )}

        {/* ---------------- check and save ---------------- */}
        {stage === STATE.CONFIRM && draft && (
          <>
            <div className="voice-head">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                {draft.missing.includes("amount") ? "How much was it?" : "Save this?"}
              </h5>
              <small className="text-muted">
                {draft.missing.includes("amount") ? "I got everything else" : "Tap any line to fix it"}
              </small>
            </div>

            <div className="voice-said-row">
              <i className="fi fi-rr-microphone"></i>
              <span>&ldquo;{heard}&rdquo;</span>
            </div>

            <div className={`voice-amount-card${draft.missing.includes("amount") ? " is-needed" : ""}`}>
              <div className="voice-amount-top">
                <label htmlFor="voiceAmount">{draft.missing.includes("amount") ? "Amount needed" : "Amount"}</label>
                {draft.missing.includes("amount") ? (
                  <span className="voice-flag is-warn">not in what you said</span>
                ) : (
                  draft.confidence >= 0.8 && <span className="voice-flag is-ok">heard clearly</span>
                )}
              </div>
              <div className="voice-amount-row">
                <span className="voice-amount-symbol">{currency}</span>
                <input
                  id="voiceAmount"
                  ref={amountRef}
                  className="voice-amount-input"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={draft.amount}
                  placeholder="0.00"
                  onChange={(e) => setField("amount", e.target.value)}
                />
              </div>
            </div>

            <div className="voice-fields">
              <div className={`voice-field${categoryUnsure ? " is-unsure" : ""}`}>
                <label htmlFor="voiceCategory">Category</label>
                <select
                  id="voiceCategory"
                  className="voice-field-control"
                  value={draft.category}
                  onChange={(e) => setField("category", e.target.value)}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                {categoryUnsure && <span className="voice-flag is-warn">check</span>}
              </div>

              <div className="voice-field">
                <label htmlFor="voiceDescription">Description</label>
                <input
                  id="voiceDescription"
                  className="voice-field-control"
                  type="text"
                  value={draft.description}
                  placeholder="Where it went"
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>

              {/* the app's own calendar, not the browser's grey one */}
              <div className="voice-field voice-field-date">
                <ThemedDatePicker
                  value={draft.date}
                  name="date"
                  label="Date"
                  onChange={(e) => setField("date", e.target.value)}
                />
              </div>
            </div>

            {categoryUnsure && (
              <div className="voice-unsure">
                <span>
                  Nothing in your categories matched that — pick the right one, or keep{" "}
                  <strong>{draft.category}</strong>.
                </span>
                <button type="button" onClick={() => setField("category", draft.category)}>
                  Keep {draft.category}
                </button>
              </div>
            )}

            {extra.length > 0 && (
              <div className="voice-extra">
                {extra.length} more {extra.length === 1 ? "expense" : "expenses"} heard — save this one first, then they follow.
              </div>
            )}

            <button
              type="button"
              className="btn-coral-modern voice-save-btn"
              disabled={!amountReady || !draft.description.trim() || categoryUnsure || saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await onSave(draft, extra);
                } catch (err) {
                  setError(err?.message || "Could not save that.");
                  setSaving(false);
                }
              }}
            >
              {saving ? "Saving…" : "Save expense"}
            </button>

            <button
              type="button"
              className="voice-secondary-btn"
              onClick={() => {
                setDraft(null);
                setExtra([]);
                setSentence("");
                setStage(STATE.INPUT);
                if (supported) start();
              }}
            >
              Say it again
            </button>

            {error && <div className="voice-error">{error}</div>}

            <div className="voice-footnote">
              {!amountReady
                ? "An amount is never guessed — the button waits for one"
                : categoryUnsure
                ? "Confirm the category and the button opens up"
                : "Check the amount before saving"}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceAddSheet;
